import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Avatar,
  Button,
  IconButton,
  Link
} from '@mui/material';
import {
  VideoCall as VideoCallIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  Link as LinkIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { Interview, EmailComposerData } from '../../types';
import airtableService from '../../services/airtable';
import emailService from '../../services/emailService';
import EmailComposer from '../../components/EmailComposer';

const InterviewDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [interview, setInterview] = useState<Interview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [emailComposerOpen, setEmailComposerOpen] = useState(false);

  useEffect(() => {
    const fetchInterview = async () => {
      if (!id) {
        setError('Interview ID not provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await airtableService.getInterviewById(id);

        if (response.success) {
          setInterview(response.data);
        } else {
          setError(response.error || 'Failed to fetch interview details');
        }
      } catch (err) {
        setError('An error occurred while fetching interview details');
        console.error('Error fetching interview:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchInterview();
  }, [id]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/interviews')}
          sx={{ mb: 2 }}
        >
          Back to Interviews
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!interview) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/interviews')}
          sx={{ mb: 2 }}
        >
          Back to Interviews
        </Button>
        <Alert severity="info">Interview not found</Alert>
      </Box>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Scheduled': return 'primary';
      case 'In Progress': return 'warning';
      case 'Completed': return 'success';
      case 'Cancelled': return 'error';
      case 'Rescheduled': return 'secondary';
      default: return 'default';
    }
  };

  const handleSendEmail = async (emailData: EmailComposerData) => {
    try {
      const queueResponse = await emailService.addToQueue(emailData, 'current-user-id');
      if (queueResponse.success) {
        console.log('✅ Interview email queued successfully');
      } else {
        console.error('❌ Failed to queue email:', queueResponse.error);
        throw new Error(queueResponse.error);
      }
    } catch (error) {
      console.error('Error sending interview email:', error);
      throw error;
    }
  };

  // Create a mock candidate object for the email composer
  const getInterviewCandidate = () => {
    if (!interview || !interview.candidateFirstName || !interview.candidateLastName) {
      return [];
    }

    return [{
      id: 'interview-candidate',
      firstName: interview.candidateFirstName,
      lastName: interview.candidateLastName,
      email: 'candidate@email.com', // This would need to come from the linked candidate record
      phone: '',
      currentLocation: '',
      willingToRelocate: false,
      noticePeriod: 'Immediate' as const,
      source: 'LinkedIn' as const,
      status: 'Active' as const
    }];
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <IconButton onClick={() => navigate('/interviews')} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">
            Interview Details
          </Typography>
        </Box>
        <Button
          startIcon={<EditIcon />}
          variant="outlined"
          onClick={() => {
            // TODO: Add edit functionality
            console.log('Edit interview:', interview.id);
          }}
        >
          Edit Interview
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Main Interview Information */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Typography variant="h5" gutterBottom>
                  {interview.type} - {interview.stage}
                </Typography>
                <Chip
                  label={interview.status}
                  color={getStatusColor(interview.status) as any}
                  variant="outlined"
                />
              </Box>

              <Grid container spacing={2}>
                {/* Scheduled Date & Time */}
                {interview.scheduledDate && (
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <ScheduleIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        Scheduled Date & Time
                      </Typography>
                    </Box>
                    <Typography variant="body1">
                      {interview.scheduledDate.toLocaleDateString('en-US', {
                        timeZone: 'America/Los_Angeles',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })} at {interview.scheduledTime}
                    </Typography>
                  </Grid>
                )}

                {/* Duration */}
                {interview.duration && (
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <ScheduleIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        Duration
                      </Typography>
                    </Box>
                    <Typography variant="body1">
                      {interview.duration} minutes
                    </Typography>
                  </Grid>
                )}

                {/* Location */}
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Location
                    </Typography>
                  </Box>
                  <Typography variant="body1">
                    {interview.location || 'Not specified'}
                  </Typography>
                </Grid>

                {/* Meeting Link */}
                {interview.meetingLink && (
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <VideoCallIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        Meeting Link
                      </Typography>
                    </Box>
                    <Link
                      href={interview.meetingLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ display: 'flex', alignItems: 'center' }}
                    >
                      <LinkIcon sx={{ mr: 0.5, fontSize: 16 }} />
                      Join Meeting
                    </Link>
                  </Grid>
                )}

                {/* Interviewer */}
                {interview.interviewerName && (
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        Interviewer
                      </Typography>
                    </Box>
                    <Typography variant="body1">
                      {interview.interviewerName}
                    </Typography>
                  </Grid>
                )}

                {/* Technical Assessment URL */}
                {interview.technicalAssessmentUrl && (
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <AssignmentIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        Technical Assessment
                      </Typography>
                    </Box>
                    <Link
                      href={interview.technicalAssessmentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ display: 'flex', alignItems: 'center' }}
                    >
                      <LinkIcon sx={{ mr: 0.5, fontSize: 16 }} />
                      Open Assessment
                    </Link>
                  </Grid>
                )}

                {/* Reschedule Reason */}
                {interview.rescheduleReason && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Reschedule Reason
                    </Typography>
                    <Alert severity="info" sx={{ mt: 1 }}>
                      {interview.rescheduleReason}
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Interview Notes */}
          {interview.interviewNotes && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Interview Notes
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                  }}
                >
                  {interview.interviewNotes}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Candidate Information */}
          {(interview.candidateImage || interview.candidateFirstName || interview.candidateLastName) && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Candidate
                </Typography>
                <Box display="flex" alignItems="center">
                  <Avatar
                    src={interview.candidateImage?.[0]?.url}
                    sx={{ width: 60, height: 60, mr: 2 }}
                  >
                    {interview.candidateFirstName && interview.candidateLastName
                      ? `${interview.candidateFirstName[0]}${interview.candidateLastName[0]}`
                      : <PersonIcon />
                    }
                  </Avatar>
                  <Box>
                    {(interview.candidateFirstName || interview.candidateLastName) && (
                      <Typography variant="h6" sx={{ color: 'primary.main' }}>
                        {interview.candidateFirstName} {interview.candidateLastName}
                      </Typography>
                    )}
                    {interview.candidateImage && (
                      <Typography variant="body2" color="text.secondary">
                        Profile photo available
                      </Typography>
                    )}
                    {!interview.candidateFirstName && !interview.candidateLastName && (
                      <Typography variant="body2" color="text.secondary">
                        Candidate information available
                      </Typography>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Quick Actions */}
          <Card sx={{ mt: interview.candidateImage ? 2 : 0 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                {interview.meetingLink && (
                  <Button
                    variant="contained"
                    startIcon={<VideoCallIcon />}
                    href={interview.meetingLink}
                    target="_blank"
                    fullWidth
                  >
                    Join Meeting
                  </Button>
                )}
                {interview.technicalAssessmentUrl && (
                  <Button
                    variant="outlined"
                    startIcon={<AssignmentIcon />}
                    href={interview.technicalAssessmentUrl}
                    target="_blank"
                    fullWidth
                  >
                    Open Assessment
                  </Button>
                )}
                {interview.candidateFirstName && interview.candidateLastName && (
                  <Button
                    variant="outlined"
                    startIcon={<EmailIcon />}
                    onClick={() => setEmailComposerOpen(true)}
                    fullWidth
                  >
                    Email Candidate
                  </Button>
                )}
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => {
                    // TODO: Add edit functionality
                    console.log('Edit interview:', interview.id);
                  }}
                  fullWidth
                >
                  Edit Interview
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Email Composer */}
      <EmailComposer
        open={emailComposerOpen}
        onClose={() => setEmailComposerOpen(false)}
        recipients={getInterviewCandidate()}
        onSend={handleSendEmail}
        defaultTemplate={interview?.type === 'Video Interview' ? {
          id: 'template_001',
          templateName: 'Interview Invitation',
          subjectLine: 'Interview Invitation - {positionTitle}',
          emailBody: 'Dear {firstName},\n\nYour interview is scheduled.\n\nBest regards,\nM4 Consulting',
          templateCategory: 'Interview Invitation' as const,
          variablesUsed: ['firstName'],
          active: true,
          createdBy: 'system',
          createdDate: new Date(),
          lastModified: new Date()
        } : undefined}
      />
    </Box>
  );
};

export default InterviewDetails;